import { useRef, useEffect, useState, useCallback } from 'react';
import type React from 'react';
import type { Element } from '../../types';
import type { RuntimeMedia } from '../../types';
import { shouldProtectLongVideoPlayback } from '../../utils/longVideoRuntime';
import {
  renderGradientFadeEffect,
  clearGradientFadeCache,
} from '../../masks';
import {
  processImageWithFaceBlur,
  processVideoFrameWithFaceBlur,
  detectVideoFaces,
  needsFaceBlurProcessing,
  clearFaceBlurCache,
} from '../utils/faceBlurRenderer';
import { isRenderPlaying } from '../../engine/RenderCoordinator';
import { createFrameEffectsBridge, type FrameEffectsBridge } from '../../engine/FrameEffectsWorkerBridge';

import type { OpacitySettings } from '../../types/gradientFade';
import type { GradientFadeConfig } from '../../engine/FrameEffectsWorker';

/** Max cached effect entries per cache (face blur / gradient fade).
 *  Each entry is an HTMLCanvasElement or ImageBitmap (~8 MB at 1080p). Cap prevents unbounded growth. */
const MAX_EFFECT_CACHE_ENTRIES = 20;

/**
 * Try to convert OpacitySettings into the simplified worker GradientFadeConfig.
 * Returns null when settings use features the worker can't handle (radial, mask,
 * non-cardinal angles, offsets), so the caller falls back to inline rendering.
 */
function toWorkerGradientFade(opSettings: OpacitySettings, elWidth: number, elHeight: number): GradientFadeConfig | null {
  const gradient = opSettings.gradient;
  if (!gradient || gradient.type !== 'linear') return null;
  // Only handle cardinal angles without offsets
  if (gradient.offsetX !== 0 || gradient.offsetY !== 0) return null;

  // Mask present and enabled → not supported by worker
  const mask = opSettings.mask;
  if (mask && mask.enabled && mask.paths.length > 0) return null;

  let direction: GradientFadeConfig['direction'];
  const angle = ((gradient.angle % 360) + 360) % 360; // normalize to 0-359
  if (angle === 0 || angle === 360) direction = 'right';
  else if (angle === 90) direction = 'bottom';
  else if (angle === 180) direction = 'left';
  else if (angle === 270) direction = 'top';
  else return null; // non-cardinal angle

  // Map strength (0-100) to opacity (0-1) and softness (0-100) to feather in pixels
  const opacity = Math.min(1, Math.max(0, (gradient.strength ?? 100) / 100));
  const maxDim = direction === 'top' || direction === 'bottom' ? elHeight : elWidth;
  const feather = Math.max(0, ((gradient.softness ?? 50) / 100) * maxDim);

  return { direction, opacity, feather };
}

/** Evict oldest entries from a Map until it's under the cap. Closes ImageBitmaps if present. */
function evictEffectCache<V extends HTMLCanvasElement | ImageBitmap | null>(
  cache: Map<string, V>,
  settingsCache?: Map<string, string>,
): void {
  while (cache.size > MAX_EFFECT_CACHE_ENTRIES) {
    const oldestKey = cache.keys().next().value;
    if (oldestKey === undefined) break;
    const old = cache.get(oldestKey);
    cache.delete(oldestKey);
    settingsCache?.delete(oldestKey);
    if (old && 'close' in old && typeof old.close === 'function') {
      try { old.close(); } catch { /* noop */ }
    }
  }
}

export interface UseCanvasFrameLoopsReturn {
  faceBlurCacheRef: React.MutableRefObject<Map<string, HTMLCanvasElement | ImageBitmap | null>>;
  gradientFadeCacheRef: React.MutableRefObject<Map<string, ImageBitmap | null>>;
}

export function useCanvasFrameLoops(
  elements: Element[],
  runtime: React.MutableRefObject<RuntimeMedia>,
  options?: {
    isPlaying?: boolean;
    protectedElementIds?: Set<string>;
  }
): UseCanvasFrameLoopsReturn {
  const isPlaying = options?.isPlaying === true;
  const protectedElementIds = options?.protectedElementIds;

  // Lazily-created worker bridge for offloading face-blur / gradient-fade to a Web Worker.
  // Falls back to inline processing when OffscreenCanvas is unavailable or the flag is off.
  const bridgeRef = useRef<FrameEffectsBridge | null>(null);
  if (!bridgeRef.current) {
    bridgeRef.current = createFrameEffectsBridge();
  }

  // Internal canvas map for rasterizing video frames
  const gradientFadeVideoFrameCanvasRef = useRef<Map<string, HTMLCanvasElement>>(new Map());

  const shouldGuardVideoElement = useCallback((element: Element): boolean => {
    if (element.kind !== 'video') return false;
    if (protectedElementIds?.has(element.id)) return isPlaying;
    return shouldProtectLongVideoPlayback({
      durationSec: element.preparedMedia?.duration,
      previewUrl: element.previewUrl,
      srcUrl: element.src,
      deferVideoHydration: element.deferVideoHydration,
      isPlaying,
    });
  }, [isPlaying, protectedElementIds]);

  const rasterizeVideoFrameForGradientFade = useCallback((elementId: string, sourceVideo: HTMLVideoElement, targetWidth: number, targetHeight: number): HTMLCanvasElement | null => {
    if (sourceVideo.readyState < 2 && sourceVideo.videoWidth <= 0) return null;
    let frameCanvas = gradientFadeVideoFrameCanvasRef.current.get(elementId);
    if (!frameCanvas) {
      frameCanvas = document.createElement('canvas');
      gradientFadeVideoFrameCanvasRef.current.set(elementId, frameCanvas);
    }
    const frameWidth = Math.max(1, Math.round(targetWidth || sourceVideo.videoWidth || 1));
    const frameHeight = Math.max(1, Math.round(targetHeight || sourceVideo.videoHeight || 1));
    if (frameCanvas.width !== frameWidth || frameCanvas.height !== frameHeight) {
      frameCanvas.width = frameWidth;
      frameCanvas.height = frameHeight;
    }
    const ctx = frameCanvas.getContext('2d', { willReadFrequently: true }) as CanvasRenderingContext2D | null;
    if (!ctx) return null;
    ctx.clearRect(0, 0, frameWidth, frameHeight);
    try {
      ctx.drawImage(sourceVideo, 0, 0, frameWidth, frameHeight);
      return frameCanvas;
    } catch {
      return null;
    }
  }, []);
  // Face blur processed images cache (ImageBitmap when processed by worker, HTMLCanvasElement when inline)
  const faceBlurCacheRef = useRef<Map<string, HTMLCanvasElement | ImageBitmap | null>>(new Map());
  const faceBlurSettingsRef = useRef<Map<string, string>>(new Map());
  const faceBlurProcessingRef = useRef<Set<string>>(new Set());
  const faceBlurVideoRafRef = useRef<number | null>(null);
  const [, setFaceBlurVersion] = useState(0);

  // Gradient fade effect cache
  const gradientFadeCacheRef = useRef<Map<string, ImageBitmap | null>>(new Map());
  const gradientFadeSettingsRef = useRef<Map<string, string>>(new Map());
  const gradientFadeProcessingRef = useRef<Set<string>>(new Set());
  const gradientFadeVideoRafRef = useRef<number | null>(null);
  const gradientFadeVideoRasterLogMsRef = useRef<Map<string, number>>(new Map());
  const [, setGradientFadeVersion] = useState(0);

  // Process face blur for images when settings change
  useEffect(() => {
    const processElements = async () => {
      for (const el of elements) {
        if (el.kind !== 'image') continue;

        const mediaEl = el as any;
        const settings = mediaEl.faceBlur;
        const settingsKey = JSON.stringify(settings || {});
        const cachedKey = faceBlurSettingsRef.current.get(el.id);

        if (settingsKey === cachedKey) continue;

        if (!needsFaceBlurProcessing(settings)) {
          faceBlurCacheRef.current.delete(el.id);
          faceBlurSettingsRef.current.delete(el.id);
          clearFaceBlurCache(el.id);
          continue;
        }

        if (faceBlurProcessingRef.current.has(el.id)) continue;

        const source = runtime.current.imageMap.get(el.id);
        if (!source) continue;

        faceBlurProcessingRef.current.add(el.id);

        try {
          const processed = await processImageWithFaceBlur(el.id, source as HTMLImageElement, settings);
          faceBlurCacheRef.current.set(el.id, processed);
          faceBlurSettingsRef.current.set(el.id, settingsKey);
          evictEffectCache(faceBlurCacheRef.current, faceBlurSettingsRef.current);
        } catch (error) {
          console.error(`Face blur processing failed for ${el.id}:`, error);
          faceBlurCacheRef.current.set(el.id, null);
        } finally {
          faceBlurProcessingRef.current.delete(el.id);
        }
      }
    };

    processElements();
  }, [elements, runtime]);

  // Process gradient fade effects for media elements (images + initial video frame)
  useEffect(() => {
    const processGradientFade = async () => {
      for (const el of elements) {
        if (el.kind !== 'image' && el.kind !== 'video') {
          continue;
        }
        const opSettings = (el as any).opacitySettings;
        if (!opSettings || opSettings.mode !== 'gradient-fade') {
          if (gradientFadeCacheRef.current.has(el.id)) {
            gradientFadeCacheRef.current.delete(el.id);
            gradientFadeSettingsRef.current.delete(el.id);
            clearGradientFadeCache(el.id);
          }
          if (el.kind === 'video') {
            gradientFadeVideoFrameCanvasRef.current.delete(el.id);
            gradientFadeVideoRasterLogMsRef.current.delete(el.id);
          }
          continue;
        }
        const settingsKey = JSON.stringify({ mask: opSettings.mask, gradient: opSettings.gradient, width: el.width, height: el.height, kind: el.kind });
        const cachedKey = gradientFadeSettingsRef.current.get(el.id);
        if (settingsKey === cachedKey) {
          continue;
        }
        if (gradientFadeProcessingRef.current.has(el.id)) {
          continue;
        }
        let source: HTMLImageElement | HTMLVideoElement | HTMLCanvasElement | OffscreenCanvas | ImageBitmap | null = null;
        if (el.kind === 'image') {
          source = runtime.current.imageMap.get(el.id) ?? null;
        } else {
          if (shouldGuardVideoElement(el)) {
            gradientFadeCacheRef.current.delete(el.id);
            gradientFadeSettingsRef.current.delete(el.id);
            clearGradientFadeCache(el.id);
            gradientFadeVideoFrameCanvasRef.current.delete(el.id);
            gradientFadeVideoRasterLogMsRef.current.delete(el.id);
            continue;
          }
          const sourceVideo = runtime.current.webCodecsCanvasMap?.get(el.id) ?? runtime.current.videoMap.get(el.id);
          if (!sourceVideo) continue;
          source = sourceVideo instanceof HTMLVideoElement
            ? rasterizeVideoFrameForGradientFade(el.id, sourceVideo, el.width, el.height)
            : sourceVideo;
        }
        if (!source) {
          continue;
        }
        gradientFadeProcessingRef.current.add(el.id);
        try {
          const bitmap = await renderGradientFadeEffect(
            el.id,
            source,
            opSettings,
            el.width,
            el.height,
            el.kind === 'video'
          );
          gradientFadeCacheRef.current.set(el.id, bitmap);
          gradientFadeSettingsRef.current.set(el.id, settingsKey);
          evictEffectCache(gradientFadeCacheRef.current, gradientFadeSettingsRef.current);
          setGradientFadeVersion(v => v + 1);
        } catch {
          gradientFadeCacheRef.current.set(el.id, null);
        } finally {
          gradientFadeProcessingRef.current.delete(el.id);
        }
      }
    };
    processGradientFade();
  }, [elements, runtime, rasterizeVideoFrameForGradientFade, shouldGuardVideoElement]);

  // Process gradient fade for videos - continuous frame updates (30fps RAF loop)
  useEffect(() => {
    const videoElementsWithGradientFade = elements.filter(
      (el) =>
        el.kind === 'video' &&
        (el as any).opacitySettings?.mode === 'gradient-fade' &&
        !shouldGuardVideoElement(el)
    );
    const videoElementsWithGradientFadeSet = new Set(videoElementsWithGradientFade.map((el) => el.id));
    for (const el of elements) {
      if (el.kind !== 'video') continue;
      const opSettings = (el as any).opacitySettings;
      if (!opSettings || opSettings.mode !== 'gradient-fade') {
        if (gradientFadeCacheRef.current.has(el.id)) {
          gradientFadeCacheRef.current.delete(el.id);
          gradientFadeSettingsRef.current.delete(el.id);
          clearGradientFadeCache(el.id);
        }
        gradientFadeVideoFrameCanvasRef.current.delete(el.id);
        gradientFadeVideoRasterLogMsRef.current.delete(el.id);
      }
    }
    for (const elementId of gradientFadeVideoFrameCanvasRef.current.keys()) {
      if (videoElementsWithGradientFadeSet.has(elementId)) continue;
      gradientFadeVideoFrameCanvasRef.current.delete(elementId);
      gradientFadeVideoRasterLogMsRef.current.delete(elementId);
    }
    if (videoElementsWithGradientFade.length === 0) {
      if (gradientFadeVideoRafRef.current !== null) {
        cancelAnimationFrame(gradientFadeVideoRafRef.current);
        gradientFadeVideoRafRef.current = null;
      }
      return;
    }
    let lastFrameTime = 0;
    const frameInterval = 1000 / 30;
    const updateVideoGradientFade = async (timestamp: number) => {
      // During playback, RenderCoordinator Phase 3/4 handles all frame processing.
      // This loop only runs when paused/scrubbing for immediate visual feedback.
      if (isRenderPlaying()) {
        gradientFadeVideoRafRef.current = requestAnimationFrame(updateVideoGradientFade);
        return;
      }
      if (timestamp - lastFrameTime < frameInterval) {
        gradientFadeVideoRafRef.current = requestAnimationFrame(updateVideoGradientFade);
        return;
      }
      lastFrameTime = timestamp;
      for (const el of videoElementsWithGradientFade) {
        if (gradientFadeProcessingRef.current.has(el.id)) continue;
        const opSettings = (el as any).opacitySettings;
        if (!opSettings || opSettings.mode !== 'gradient-fade') continue;
        const source = runtime.current.webCodecsCanvasMap?.get(el.id) ?? runtime.current.videoMap.get(el.id);
        if (!source) continue;
        // Skip re-processing when video is paused — frame hasn't changed
        if (source instanceof HTMLVideoElement && source.paused) continue;
        const fxInputSource = source instanceof HTMLVideoElement
          ? rasterizeVideoFrameForGradientFade(el.id, source, el.width, el.height)
          : source;
        if (!fxInputSource) continue;
        gradientFadeProcessingRef.current.add(el.id);
        try {
          // Worker path: offload gradient-fade rendering when bridge is available
          if (bridgeRef.current?.isAvailable()) {
            const workerConfig = toWorkerGradientFade(opSettings, el.width, el.height);
            if (workerConfig) {
              try {
                const bmp = await createImageBitmap(source as HTMLVideoElement | HTMLCanvasElement);
                const result = await bridgeRef.current.processFrame(
                  el.id, bmp, el.width, el.height,
                  { gradientFade: workerConfig }
                );
                if (result) {
                  gradientFadeCacheRef.current.set(el.id, result);
                  evictEffectCache(gradientFadeCacheRef.current, gradientFadeSettingsRef.current);
                  setGradientFadeVersion(v => v + 1);
                  continue; // skip inline processing
                }
              } catch { /* fall through to inline */ }
            }
          }
          // Inline fallback (existing code)
          const bitmap = await renderGradientFadeEffect(
            el.id,
            fxInputSource,
            opSettings,
            el.width,
            el.height,
            true
          );
          gradientFadeCacheRef.current.set(el.id, bitmap);
          evictEffectCache(gradientFadeCacheRef.current, gradientFadeSettingsRef.current);
          setGradientFadeVersion(v => v + 1);
        } catch {
          // Silently fail for video frames to avoid console spam
        } finally {
          gradientFadeProcessingRef.current.delete(el.id);
        }
      }
      gradientFadeVideoRafRef.current = requestAnimationFrame(updateVideoGradientFade);
    };
    gradientFadeVideoRafRef.current = requestAnimationFrame(updateVideoGradientFade);
    return () => {
      if (gradientFadeVideoRafRef.current !== null) {
        cancelAnimationFrame(gradientFadeVideoRafRef.current);
        gradientFadeVideoRafRef.current = null;
      }
    };
  }, [elements, runtime, rasterizeVideoFrameForGradientFade, shouldGuardVideoElement]);

  // Process face blur for videos - continuous frame updates (15fps RAF loop)
  useEffect(() => {
    const videoElementsWithFaceBlur = elements.filter(el => {
      if (el.kind !== 'video') return false;
      const settings = (el as any).faceBlur;
      return needsFaceBlurProcessing(settings) && !shouldGuardVideoElement(el);
    });

    for (const el of elements) {
      if (el.kind !== 'video') continue;
      const settings = (el as any).faceBlur;
      if (!needsFaceBlurProcessing(settings) || shouldGuardVideoElement(el)) {
        if (faceBlurCacheRef.current.has(el.id)) {
          faceBlurCacheRef.current.delete(el.id);
          faceBlurSettingsRef.current.delete(el.id);
          clearFaceBlurCache(el.id);
        }
      }
    }

    if (videoElementsWithFaceBlur.length === 0) {
      if (faceBlurVideoRafRef.current !== null) {
        cancelAnimationFrame(faceBlurVideoRafRef.current);
        faceBlurVideoRafRef.current = null;
      }
      return;
    }

    let lastFrameTime = 0;
    const frameInterval = 1000 / 15;

    const updateVideoFaceBlur = async (timestamp: number) => {
      // During playback, RenderCoordinator Phase 3/4 handles all frame processing.
      // This loop only runs when paused/scrubbing for immediate visual feedback.
      if (isRenderPlaying()) {
        faceBlurVideoRafRef.current = requestAnimationFrame(updateVideoFaceBlur);
        return;
      }
      if (timestamp - lastFrameTime < frameInterval) {
        faceBlurVideoRafRef.current = requestAnimationFrame(updateVideoFaceBlur);
        return;
      }
      lastFrameTime = timestamp;

      for (const el of videoElementsWithFaceBlur) {
        if (faceBlurProcessingRef.current.has(el.id)) continue;

        const settings = (el as any).faceBlur;
        if (!needsFaceBlurProcessing(settings)) continue;

        const source = runtime.current.webCodecsCanvasMap?.get(el.id) ?? runtime.current.videoMap.get(el.id);
        if (!source) continue;
        // Skip re-processing when video is paused — frame hasn't changed
        if (source instanceof HTMLVideoElement && source.paused) continue;

        faceBlurProcessingRef.current.add(el.id);

        try {
          // Worker path: detect faces inline, then offload blur rendering to worker
          if (bridgeRef.current?.isAvailable()) {
            try {
              const faces = await detectVideoFaces(el.id, source, settings);
              if (faces.length > 0) {
                const regions = faces.map(f => ({
                  x: f.box.x,
                  y: f.box.y,
                  width: f.box.width,
                  height: f.box.height,
                }));
                const bitmap = await createImageBitmap(source as HTMLVideoElement | HTMLCanvasElement);
                const result = await bridgeRef.current.processFrame(
                  el.id, bitmap, el.width, el.height,
                  { faceBlur: { regions, blurRadius: settings.intensity ?? 20 } }
                );
                if (result) {
                  faceBlurCacheRef.current.set(el.id, result);
                  evictEffectCache(faceBlurCacheRef.current, faceBlurSettingsRef.current);
                  setFaceBlurVersion(v => v + 1);
                  continue; // skip inline processing
                }
              }
            } catch { /* fall through to inline */ }
          }
          // Inline fallback (existing code)
          const processed = await processVideoFrameWithFaceBlur(el.id, source, settings);
          faceBlurCacheRef.current.set(el.id, processed ?? null);
          evictEffectCache(faceBlurCacheRef.current, faceBlurSettingsRef.current);
          setFaceBlurVersion(v => v + 1);
        } catch {
          // Silently fail for video frames to avoid console spam
        } finally {
          faceBlurProcessingRef.current.delete(el.id);
        }
      }

      faceBlurVideoRafRef.current = requestAnimationFrame(updateVideoFaceBlur);
    };

    faceBlurVideoRafRef.current = requestAnimationFrame(updateVideoFaceBlur);

    return () => {
      if (faceBlurVideoRafRef.current !== null) {
        cancelAnimationFrame(faceBlurVideoRafRef.current);
        faceBlurVideoRafRef.current = null;
      }
    };
  }, [elements, runtime, shouldGuardVideoElement]);

  // Terminate the worker bridge on unmount to release resources
  useEffect(() => {
    return () => {
      bridgeRef.current?.terminate();
    };
  }, []);

  return {
    faceBlurCacheRef,
    gradientFadeCacheRef,
  };
}
